import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  MessageCircle, X, Send, Loader2, Bot, User, 
  AlertTriangle, CheckCircle, Lightbulb, RefreshCw,
  ChevronDown, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

// Common issues and solutions database
const TROUBLESHOOTING_DB = {
  'login': {
    keywords: ['login', 'sign in', 'password', 'email', 'access', 'cant login', 'unable to login'],
    solutions: [
      'Make sure you\'re using the correct email and password',
      'Check if Caps Lock is on',
      'Try clearing your browser cache and cookies',
      'If you forgot your password, contact admin@massmasterpro.com',
      'Make sure you have an active subscription'
    ]
  },
  'calculation': {
    keywords: ['calculate', 'wrong', 'incorrect', 'math', 'result', 'number', 'total'],
    solutions: [
      'Double-check your input values for typos',
      'Make sure you\'re using the correct units (feet vs inches)',
      'Verify your material selections are correct',
      'Try clearing the form and re-entering values',
      'Check if cost per ton/unit is properly set'
    ]
  },
  'export': {
    keywords: ['download', 'export', 'pdf', 'excel', 'word', 'email', 'save'],
    solutions: [
      'Check if pop-up blocker is preventing downloads',
      'Try a different browser (Chrome recommended)',
      'Make sure you have data entered before exporting',
      'For email issues, verify the recipient email is correct',
      'Check your Downloads folder for the file'
    ]
  },
  'loading': {
    keywords: ['slow', 'loading', 'stuck', 'freeze', 'not responding', 'blank'],
    solutions: [
      'Refresh the page (Ctrl+R or Cmd+R)',
      'Clear browser cache and cookies',
      'Check your internet connection',
      'Try disabling browser extensions',
      'Use Chrome or Firefox for best performance'
    ]
  },
  'mobile': {
    keywords: ['phone', 'mobile', 'tablet', 'touch', 'small screen', 'responsive'],
    solutions: [
      'The app is fully responsive - try rotating your device',
      'Pinch to zoom if text is too small',
      'Use the PWA install option for better mobile experience',
      'On iOS, add to Home Screen for app-like experience',
      'Clear mobile browser cache if issues persist'
    ]
  },
  'data': {
    keywords: ['lost', 'data', 'saved', 'disappeared', 'missing', 'restore'],
    solutions: [
      'Use the Save feature in the header to save your work',
      'Data is stored locally - clearing browser data will erase it',
      'Export your estimates regularly as backup',
      'Check if you\'re logged into the correct account',
      'Try the Load feature to restore a saved project'
    ]
  },
  'ai': {
    keywords: ['ai', 'analysis', 'analyze', 'smart', 'recommendation', 'suggestion'],
    solutions: [
      'Make sure you\'ve entered a valid project location',
      'AI analysis requires an internet connection',
      'Try a different project type selection',
      'Wait a few seconds for AI response',
      'If AI is unavailable, use manual calculations'
    ]
  }
};

// Quick action suggestions
const QUICK_ACTIONS = [
  { label: 'Calculator not working', topic: 'calculation' },
  { label: 'Can\'t download/export', topic: 'export' },
  { label: 'Login problems', topic: 'login' },
  { label: 'App is slow', topic: 'loading' },
  { label: 'Lost my data', topic: 'data' },
  { label: 'AI not responding', topic: 'ai' }
];

const AITroubleshooter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      content: 'Hi! I\'m your AI assistant. How can I help you today? Click a quick action below or describe your issue.',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const findSolutions = useCallback((query) => {
    const queryLower = query.toLowerCase();
    let bestMatch = null;
    let maxScore = 0;

    for (const [topic, data] of Object.entries(TROUBLESHOOTING_DB)) {
      const score = data.keywords.filter(kw => queryLower.includes(kw)).length;
      if (score > maxScore) {
        maxScore = score;
        bestMatch = { topic, ...data };
      }
    }

    return bestMatch;
  }, []);

  const handleSendMessage = useCallback(async (message = inputValue) => {
    if (!message.trim()) return;

    // Add user message
    const userMessage = {
      type: 'user',
      content: message,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate AI thinking
    await new Promise(resolve => setTimeout(resolve, 800));

    // Find matching solutions
    const match = findSolutions(message);

    let botResponse;
    if (match) {
      botResponse = {
        type: 'bot',
        content: `I found some solutions for your ${match.topic} issue:`,
        solutions: match.solutions,
        topic: match.topic,
        timestamp: new Date()
      };
    } else {
      botResponse = {
        type: 'bot',
        content: 'I\'m not sure about that specific issue. Here are some general tips:',
        solutions: [
          'Try refreshing the page',
          'Clear your browser cache',
          'Make sure you\'re using a modern browser',
          'Check your internet connection',
          'Contact support at admin@massmasterpro.com'
        ],
        timestamp: new Date()
      };
    }

    setMessages(prev => [...prev, botResponse]);
    setIsTyping(false);
  }, [inputValue, findSolutions]);

  const handleQuickAction = (topic) => {
    const data = TROUBLESHOOTING_DB[topic];
    if (data) {
      handleSendMessage(`I need help with ${topic}`);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen 
            ? 'bg-slate-700 hover:bg-slate-600' 
            : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
        }`}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
        {!isOpen && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
            <Sparkles className="w-3 h-3 text-white" />
          </span>
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-40 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-card border border-border rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-5">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-white">AI Support</h3>
                <p className="text-xs text-blue-100">Always here to help</p>
              </div>
              <Badge variant="outline" className="bg-green-500/20 text-green-100 border-green-400">
                Online
              </Badge>
            </div>
          </div>

          {/* Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-4 bg-muted/30">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[85%] ${msg.type === 'user' ? 'order-2' : 'order-1'}`}>
                  <div
                    className={`rounded-2xl p-3 ${
                      msg.type === 'user'
                        ? 'bg-blue-600 text-white rounded-br-sm'
                        : 'bg-card border border-border rounded-bl-sm'
                    }`}
                  >
                    <p className="text-sm">{msg.content}</p>
                    
                    {msg.solutions && (
                      <ul className="mt-3 space-y-2">
                        {msg.solutions.map((solution, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <CheckCircle className={`w-4 h-4 mt-0.5 shrink-0 ${
                              msg.type === 'user' ? 'text-blue-200' : 'text-green-500'
                            }`} />
                            <span>{solution}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <p className={`text-xs text-muted-foreground mt-1 ${
                    msg.type === 'user' ? 'text-right' : 'text-left'
                  }`}>
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-card border border-border rounded-2xl rounded-bl-sm p-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div className="p-3 border-t border-border bg-card/50">
            <p className="text-xs text-muted-foreground mb-2">Quick help:</p>
            <div className="flex flex-wrap gap-1">
              {QUICK_ACTIONS.map((action, i) => (
                <button
                  key={i}
                  onClick={() => handleQuickAction(action.topic)}
                  className="text-xs px-2 py-1 rounded-full bg-muted hover:bg-muted/80 transition-colors"
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="p-3 border-t border-border bg-card">
            <div className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Describe your issue..."
                className="flex-1"
              />
              <Button 
                onClick={() => handleSendMessage()} 
                disabled={!inputValue.trim() || isTyping}
                size="icon"
              >
                {isTyping ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AITroubleshooter;
